import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/store/auth-store';

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export const useAuthRefresh = () => {
  const refreshUserData = useAuthStore((state) => state.refreshUserData);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const lastRefreshTime = useRef<number>(0);
  const REFRESH_COOLDOWN = 30000; // 30 seconds minimum between refreshes

  // Debounced refresh function to prevent too many calls
  const debouncedRefresh = useRef(
    debounce(async () => {
      const now = Date.now();
      
      // Check if enough time has passed since last refresh
      if (now - lastRefreshTime.current < REFRESH_COOLDOWN) {
        return;
      }

      try {
        await refreshUserData();
        lastRefreshTime.current = now;
      } catch (error) {
        console.warn('Failed to refresh user data on visibility change:', error);
      }
    }, 1000) // 1 second debounce
  ).current;

  useEffect(() => {
    // Only set up listeners if user is authenticated
    if (!isAuthenticated) {
      return;
    }

    const handleVisibilityChange = () => {
      // Only refresh when tab becomes visible
      if (!document.hidden) {
        debouncedRefresh();
      }
    };

    const handleFocus = () => {
      debouncedRefresh();
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, debouncedRefresh]);

  // Return the refresh function for manual use
  return {
    refreshUserData: debouncedRefresh,
  };
};
